import type { Address } from '@Types/Bank.interface';

/**
 * Converts a string to title case — matches the old flow's formatAddress/formatCity
 * Vue filter algorithm from certifid-v2-webapp-frontend/src/filters.js.
 *
 * e.g. "123 MAIN STREET" → "123 Main Street"
 */
export const toTitleCase = (value: string | null | undefined): string => {
    if (!value) return '';
    return value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Formats an address street line (address1/address2) to title case.
 *
 * e.g. "123 MAIN ST" → "123 Main St"
 */
export const formatAddressLine = (value: string | null | undefined): string => {
    return toTitleCase(value);
};

/**
 * Formats the city/state/zip portion of an address.
 * City is title-cased to match the old flow's formatCity filter.
 *
 * e.g. { city: "ANN ARBOR", state: "MI", postalCode: "48104" } → "Ann Arbor, MI 48104"
 */
export const formatCityStateZip = (address: Address | null | undefined): string => {
    if (!address?.city) return '';
    return `${toTitleCase(address.city)}, ${address.state} ${address.postalCode}`;
};

/**
 * The display lines for an address: street, then apartment or suite when there is
 * one, then the city/state/zip line. Blank parts are dropped.
 *
 * Every part is optional, and a partial address still renders whatever it has —
 * an address holding only a state and zip is real captured data and must not
 * disappear from the screen.
 *
 * Deliberately does not title-case: these lines replace inline helpers that did
 * not, so adopting toTitleCase here would change rendered output on live screens.
 */
export const formatAddressLines = (address: Address | null | undefined): string[] => {
    if (!address) return [];

    const region = [address.state?.trim(), address.postalCode?.trim()]
        .filter(Boolean)
        .join(' ');
    const cityStateZip = [address.city?.trim(), region].filter(Boolean).join(', ');

    return [address.address1?.trim(), address.address2?.trim(), cityStateZip]
        .filter((line): line is string => !!line);
};

/**
 * Composable that returns all address formatting utilities.
 */
export const useAddressFormatter = () => {
    return {
        toTitleCase,
        formatAddressLine,
        formatCityStateZip,
        formatAddressLines,
    };
};

export default useAddressFormatter;